import { useEffect, useState } from "react";
import { motion, useScroll } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const links = [
  { label: "How it works", href: "#how-it-works", id: "nav-how-it-works" },
  { label: "About", href: "#about", id: "nav-about" },
  { label: "Reviews", href: "#reviews", id: "nav-reviews" },
  { label: "FAQ", href: "#faq", id: "nav-faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#09090B]/95 border-b border-[#F3F0E8]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between transition-all duration-500 ${
          scrolled ? "h-14" : "h-20"
        }`}
      >
        <a href="#top" data-testid="nav-logo" className="flex items-baseline gap-1.5">
          <span
            className={`font-serif-d font-black tracking-tight text-[#F3F0E8] transition-all duration-500 ${
              scrolled ? "text-xl" : "text-2xl"
            }`}
          >
            CtrlB<span className="text-[#118C4F]">.</span>
          </span>
        </a>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className={`text-[#F3F0E8]/60 hover:text-[#F3F0E8] transition-all duration-500 lowercase tracking-wide ${
                scrolled ? "text-xs" : "text-sm"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className={`hidden sm:inline-flex items-center gap-2 bg-[#F3F0E8] text-[#09090B] hover:bg-[#118C4F] hover:text-[#09090B] font-medium transition-all duration-500 ${
              scrolled ? "text-xs px-4 py-2" : "text-sm px-5 py-2.5"
            }`}
          >
            Book a discover call <ArrowRight size={14} strokeWidth={2.2} />
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#F3F0E8] p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#118C4F] origin-left"
        aria-hidden="true"
      />

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#09090B] border-t border-[#F3F0E8]/10">
          <nav aria-label="Mobile" className="px-6 py-6 flex flex-col">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`${l.id}-mobile`}
                onClick={() => setOpen(false)}
                className="font-serif-d font-semibold text-3xl tracking-tight text-[#F3F0E8] py-4 border-b border-[#F3F0E8]/8"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-button-mobile"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-[#118C4F] text-[#09090B] font-medium px-5 py-4"
            >
              Book a discover call <ArrowRight size={16} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
